import { useEffect, useState } from 'react';
import { useFeatureFlagEnabled, useFeatureFlagVariantKey } from '@posthog/react';

import { isPostHogEnabled, posthog } from '@/posthog';

export type FeatureFlagState = 'loading' | 'enabled' | 'disabled';

export type FeatureFlagVariantState = 'loading' | { variant: string | boolean | undefined };

/**
 * Resolves a PostHog boolean flag into a `'loading' | 'enabled' | 'disabled'` tri-state.
 *
 * `useFeatureFlagEnabled` returns `undefined` both while loading AND when a flag is
 * absent, so `undefined` can't mean "loading" (a missing flag would spin forever). We
 * detect load completion via `onFeatureFlags` and read the value only afterwards, so a
 * resolved-but-absent flag fails closed to `'disabled'`.
 *
 * When PostHog is disabled (local dev / E2E) flags never load: resolve to `'enabled'`
 * in Vite dev mode so the flow stays reachable, `'disabled'` otherwise.
 */
export function useFeatureFlagState(flagKey: string): FeatureFlagState {
  const isEnabled = useFeatureFlagEnabled(flagKey);
  const flagsLoaded = useFeatureFlagsLoaded();

  if (!isPostHogEnabled) {
    return import.meta.env.DEV ? 'enabled' : 'disabled';
  }

  if (!flagsLoaded) {
    return 'loading';
  }

  return isEnabled ? 'enabled' : 'disabled';
}

/**
 * Resolves a PostHog multivariate flag into `'loading' | { variant }`.
 *
 * Same load-race guard as `useFeatureFlagState`: `useFeatureFlagVariantKey` returns
 * `undefined` both while loading and when a flag is absent, so callers must wait for
 * `'loading'` to clear before treating `variant` as authoritative.
 *
 * When PostHog is disabled, returns `{ variant: undefined }` immediately (never
 * `'loading'`) so callers can apply their own domain fallback.
 */
export function useFeatureFlagVariantState(flagKey: string): FeatureFlagVariantState {
  const variant = useFeatureFlagVariantKey(flagKey);
  const flagsLoaded = useFeatureFlagsLoaded();

  if (!isPostHogEnabled) {
    return { variant: undefined };
  }

  if (!flagsLoaded) {
    return 'loading';
  }

  return { variant };
}

/**
 * True once PostHog has finished loading feature flags.
 *
 * Prefer `useFeatureFlagState` / `useFeatureFlagVariantState`, which already gate on this.
 * Use directly only when open-coding against `useFeatureFlagEnabled` /
 * `useFeatureFlagVariantKey`: both return `undefined` while loading and when a flag is
 * absent. When PostHog is disabled, skip this and use a local fallback instead.
 */
export function useFeatureFlagsLoaded(): boolean {
  const [loaded, setLoaded] = useState(() => posthog.featureFlags?.hasLoadedFlags ?? false);

  useEffect(() => {
    if (loaded || !isPostHogEnabled) {
      return;
    }

    if (posthog.featureFlags?.hasLoadedFlags) {
      setLoaded(true);
      return;
    }

    return posthog.onFeatureFlags(() => setLoaded(true));
  }, [loaded]);

  return loaded;
}
